/** A normalised (0-1) rectangle, web/top-left origin. */
export interface FracRect { x: number; y: number; w: number; h: number }

/** The gutter right of the last card column: only the page, the quick-scroll pill, or the HUD card ever paint here. */
const GUTTER_X0 = 0.978;
const GUTTER_X1 = 0.996;
/** The pill body is the page lightened by a white overlay: brighter and paler than the page. */
// 10, not 18 (2026-09-25 Galaxy frames): on a pale, all-blank Purified page
// the pill body is only +15 lum over the gutter, while the page's own vertical
// gradient reaches +13 at the top. The chroma drop below is what separates the
// two (the pill rows drop 0.069, the bright page rows 0.01), so the lift only
// has to clear the noise.
const BODY_MIN_LUM_LIFT = 10;
const BODY_MIN_CHROMA_DROP = 0.06;
/** Pill height as a fraction of the frame height (device: 0.055). */
const PILL_MIN_H = 0.035;
const PILL_MAX_H = 0.085;
/** The filter-pill row starts at ~0.82; a right-edge filter pill ("G-MA") looks the same, so the pill must sit above it. */
const PILL_MAX_BOTTOM = 0.8;
const PILL_MIN_TOP = 0.05;
/** Chevron glyphs: pixels this much darker than the pill body. */
const GLYPH_MIN_LUM_DROP = 40;
/** Where the chevrons sit (frame fractions), and the glyph area each half must show (fraction of the frame's pixels). */
const GLYPH_X0 = 0.93;
const GLYPH_X1 = 0.985;
const GLYPH_MIN_AREA_FRAC = 0.00002;
/** Columns the pill body must fill at its middle rows. */
const BODY_X0 = 0.93;
/** The pill's width on the device frames (0.084 measured); its left cap is rounded. */
const PILL_NOMINAL_W = 0.085;

export function detectQuickScrollPill(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): FracRect | null {
  if (width < 40 || height < 80) return null;
  const gx0 = Math.floor(width * GUTTER_X0), gx1 = Math.max(gx0 + 2, Math.floor(width * GUTTER_X1));
  const rowLum = new Float32Array(height), rowChr = new Float32Array(height);
  for (let y = 0; y < height; y++) {
    let l = 0, c = 0, n = 0;
    for (let x = gx0; x < gx1; x++) {
      const i = (y * width + x) * 4; const r = data[i], g = data[i + 1], b = data[i + 2];
      l += 0.2126 * r + 0.7152 * g + 0.0722 * b;
      const mx = Math.max(r, g, b), mn = Math.min(r, g, b); c += mx ? (mx - mn) / mx : 0; n++;
    }
    rowLum[y] = l / n; rowChr[y] = c / n;
  }
  // Page baseline: medians over the grid band.
  const yA = Math.floor(height * 0.2), yB = Math.floor(height * 0.8);
  const ls = Array.from(rowLum.subarray(yA, yB)).sort((a, b) => a - b), cs = Array.from(rowChr.subarray(yA, yB)).sort((a, b) => a - b);
  const baseLum = ls[ls.length >> 1], baseChr = cs[cs.length >> 1];
  // Contiguous runs of "lifted" rows.
  const lifted = (y: number) => rowLum[y] >= baseLum + BODY_MIN_LUM_LIFT && rowChr[y] <= baseChr - BODY_MIN_CHROMA_DROP;
  const yLo = Math.floor(height * PILL_MIN_TOP), yHi = Math.floor(height * PILL_MAX_BOTTOM);
  let y = yLo;
  while (y < yHi) {
    if (!lifted(y)) { y++; continue; }
    // A chevron stroke reaching the gutter breaks the lifted run for a few rows: bridge short gaps.
    const rowGap = Math.max(2, Math.floor(height * 0.012));
    let top = y, bot = y;
    while (y < yHi) {
      if (lifted(y)) { bot = y; y++; continue; }
      let k = y + 1; while (k < yHi && k - y <= rowGap && !lifted(k)) k++;
      if (k < yHi && k - y <= rowGap && lifted(k)) { y = k; continue; }
      break;
    }
    y = bot + 1;
    const h = bot - top + 1;
    if (h < height * PILL_MIN_H || h > height * PILL_MAX_H) continue;
    const bodyLum = (rowLum[top + (h >> 1)] + rowLum[top + (h >> 2)] + rowLum[bot - (h >> 2)]) / 3;
    // The chevron glyphs: dark strokes in the card's right padding / the gutter
    // (x 0.93-0.985), one in the upper half of the pill and one in the lower.
    // Nothing the page draws there is dark: cards end at ~0.975 and sprites stay
    // inside their card. The pill body itself may lie over a card face that is
    // nearly as pale as the body, so the glyphs, not the body's outline, are
    // what identifies it.
    const gx = Math.floor(width * GLYPH_X0), gxEnd = Math.floor(width * GLYPH_X1);
    let upper = 0, lower = 0, glyphLeft = width;
    for (let yy = top + 1; yy < bot - 1; yy++) for (let x = gx; x < gxEnd; x++) {
      const i = (yy * width + x) * 4; const l = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      if (l <= bodyLum - GLYPH_MIN_LUM_DROP) { if (yy < top + (h >> 1)) upper++; else lower++; if (x < glyphLeft) glyphLeft = x; }
    }
    const minGlyph = Math.max(3, Math.round(width * height * GLYPH_MIN_AREA_FRAC));
    if (upper < minGlyph || lower < minGlyph) continue;
    // The body between the glyph rows and the right edge is pill (lifted, paler), not page.
    let bodyOk = 0, bodyN = 0;
    for (const yy of [top + Math.floor(h * 0.45), top + Math.floor(h * 0.5), top + Math.floor(h * 0.55)]) for (let x = Math.floor(width * BODY_X0); x < gxEnd; x++) {
      const i = (yy * width + x) * 4; const r = data[i], g = data[i + 1], b = data[i + 2];
      const l = 0.2126 * r + 0.7152 * g + 0.0722 * b; const mx = Math.max(r, g, b), mn = Math.min(r, g, b); const c = mx ? (mx - mn) / mx : 0;
      bodyN++; if ((l >= baseLum + BODY_MIN_LUM_LIFT && c <= baseChr - BODY_MIN_CHROMA_DROP) || l <= bodyLum - GLYPH_MIN_LUM_DROP) bodyOk++;
    }
    if (bodyOk < bodyN * 0.6) continue;
    const w = Math.round(width * PILL_NOMINAL_W);
    return { x: (width - w) / width, y: top / height, w: w / width, h: h / height };
  }
  return null;
}
